/* MolCellQuest 問題データ — 細胞周期・分裂・細胞死
   ★ 配列に追記すればそのまま反映されます（id は全体で一意に）。
   ★ diff : 80〜320。基礎 0-179 / 標準 180-269 / 応用 270+ */

const CAT = { track: "cell", category: "cellcycle", categoryName: "細胞周期・分裂・細胞死" };

export const CELL_CYCLE = [
  {
    id: "cyc01", ...CAT,
    title: "細胞周期の各期", diff: 90, tags: ["細胞周期", "S期", "G1期"],
    explain: `<p>体細胞の細胞周期において、DNAの複製が起こるのはどの時期か。</p>`,
    type: "radio",
    options: ["G1期", "S期", "G2期", "M期"],
    answer: 1,
    hints: ["S は Synthesis（合成）の頭文字です。", "M期は分裂（Mitosis）、G は Gap（間隙）を意味します。"],
    solution: `<p><b>【ステップ1】細胞周期の構成</b></p>
               <table>
                 <tr><th>期</th><th>名前の由来</th><th>起こること</th><th>DNA量</th></tr>
                 <tr><td>G1</td><td>Gap 1</td><td>成長、タンパク質合成、分裂するかの判断</td><td>2n</td></tr>
                 <tr><td><b>S</b></td><td><b>Synthesis</b></td><td><b>DNA複製</b></td><td>2n → 4n</td></tr>
                 <tr><td>G2</td><td>Gap 2</td><td>分裂の準備、複製の点検</td><td>4n</td></tr>
                 <tr><td>M</td><td>Mitosis</td><td>核分裂と細胞質分裂</td><td>4n → 2n ずつ</td></tr>
               </table>
               <p><b>【ステップ2】G0期も押さえる</b></p>
               <p>分裂を続けない細胞は G1期から <b>G0期</b>（静止期）に入ります。
               神経細胞や心筋細胞は事実上ここに留まり続けます。
               肝細胞のように、必要になれば G1 に戻れるものもあります。</p>
               <p><b>【ステップ3】時間配分の目安</b></p>
               <p>培養細胞（周期 約24時間）では G1 約11時間、S 約8時間、G2 約4時間、M 約1時間。
               <b>M期は全体のごく一部</b>で、大半は間期です。
               顕微鏡で見える分裂像が少ないのはこのためです。</p>
               <p>したがって、正解は <b>S期 (B)</b> です。</p>`,
  },

  {
    id: "cyc02", ...CAT,
    title: "体細胞分裂の各期の特徴", diff: 120, tags: ["前期", "中期", "後期"],
    explain: `<p>体細胞分裂において、姉妹染色分体が分離して両極へ移動するのはどの時期か。</p>`,
    type: "radio",
    options: ["前期", "中期", "後期", "終期"],
    answer: 2,
    hints: ["中期には染色体が赤道面に並びます。その次に何が起こるでしょう。", "「後期＝分かれる（Anaphase = Apart）」と覚えます。"],
    solution: `<p><b>【ステップ1】M期の流れ</b></p>
               <table>
                 <tr><th>時期</th><th>起こること</th></tr>
                 <tr><td>前期</td><td>染色体が凝縮、紡錘体形成が始まる、核膜が崩れはじめる</td></tr>
                 <tr><td>前中期</td><td>核膜が完全に崩壊し、微小管が動原体を捕捉する</td></tr>
                 <tr><td>中期</td><td>染色体が<b>赤道面</b>に整列する</td></tr>
                 <tr><td><b>後期</b></td><td><b>コヒーシンが切断され、姉妹染色分体が両極へ分離</b></td></tr>
                 <tr><td>終期</td><td>核膜が再形成、染色体がほどける</td></tr>
                 <tr><td>細胞質分裂</td><td>収縮環がくびり切る</td></tr>
               </table>
               <p><b>【ステップ2】後期に起こることの実体</b></p>
               <p>姉妹染色分体は <b>コヒーシン</b> という輪で束ねられています。
               全染色体が正しく両極から捕捉されると
               <b>APC/C</b> が活性化してセキュリンを分解し、
               解き放たれた <b>セパラーゼ</b> がコヒーシンを切断します。
               この瞬間に染色分体が一斉に分かれます。</p>
               <p><b>【ステップ3】前期と間違えやすい点</b></p>
               <p>「染色体が2本の染色分体に見える」のは前期からですが、
               <b>分かれる</b>のは後期です。見えることと分離することは別だと区別しましょう。</p>
               <p>したがって、正解は <b>後期 (C)</b> です。</p>`,
  },

  {
    id: "cyc03", ...CAT,
    title: "減数分裂と体細胞分裂の違い", diff: 150, tags: ["減数分裂", "相同染色体", "組換え"],
    explain: `<p>減数分裂に特有で、体細胞分裂では<b>起こらない</b>ことはどれか。</p>`,
    type: "radio",
    options: [
      "染色体の凝縮",
      "第一分裂前期における相同染色体の対合と乗換え（交差）",
      "紡錘体の形成",
      "姉妹染色分体の分離",
    ],
    answer: 1,
    hints: [
      "体細胞分裂では、相同染色体どうしがペアを作ることはありません。",
      "減数第一分裂で分かれるのは相同染色体、第二分裂で分かれるのが姉妹染色分体です。",
    ],
    solution: `<p><b>【ステップ1】2つの分裂を比較する</b></p>
               <table>
                 <tr><th></th><th>体細胞分裂</th><th>減数分裂</th></tr>
                 <tr><td>分裂回数</td><td>1回</td><td>2回（DNA複製は1回）</td></tr>
                 <tr><td>相同染色体の対合</td><td><b>なし</b></td><td><b>あり（二価染色体）</b></td></tr>
                 <tr><td>乗換え（交差）</td><td>なし</td><td><b>あり</b></td></tr>
                 <tr><td>娘細胞</td><td>2個、2n</td><td>4個、n</td></tr>
                 <tr><td>遺伝的組成</td><td>親と同一</td><td>すべて異なる</td></tr>
               </table>
               <p><b>【ステップ2】第一分裂と第二分裂で分かれるもの</b></p>
               <ul>
                 <li><b>減数第一分裂</b>：<b>相同染色体</b>が分かれる（還元分裂、2n→n）</li>
                 <li><b>減数第二分裂</b>：<b>姉妹染色分体</b>が分かれる（体細胞分裂と同じ様式）</li>
               </ul>
               <p>選択肢 D の「姉妹染色分体の分離」は<b>両方で起こる</b>ので、
               減数分裂に特有とはいえません。</p>
               <p><b>【ステップ3】多様性を生む2つの機構</b></p>
               <ol>
                 <li><b>乗換え</b>：相同染色体間で配列を交換し、新しい組み合わせを作る</li>
                 <li><b>独立分配</b>：23対がそれぞれ独立に分配され、2<sup>23</sup> ≒ 838万通り</li>
               </ol>
               <p>ここに受精の組み合わせが掛かるので、
               同じ両親からでも遺伝的に同一の子はまず生まれません。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "cyc04", ...CAT,
    title: "サイクリンとCDK", diff: 180, tags: ["CDK", "サイクリン", "細胞周期制御"],
    explain: `<p>細胞周期を進める CDK（サイクリン依存性キナーゼ）について、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "CDK の量が周期的に変動し、サイクリンの量は一定である。",
      "サイクリンの量が周期的に変動し、CDK の量はほぼ一定である。",
      "CDK は単独で活性を持ち、サイクリンはその阻害因子である。",
      "サイクリンと CDK はいずれも周期のあいだ一定量に保たれる。",
    ],
    answer: 1,
    hints: [
      "「サイクリン」という名前の由来を考えましょう。何がサイクル（周期的変動）するのでしょうか。",
      "CDK は「サイクリン依存性」キナーゼ。単独では働けません。",
    ],
    solution: `<p><b>【ステップ1】名前が答えを含んでいる</b></p>
               <p><b>サイクリン</b>は「周期的に量が変動する（cycling）タンパク質」として発見されました。
               <b>CDK</b> は Cyclin-Dependent Kinase、つまり
               <b>サイクリンが結合しないと働けない</b>キナーゼです。</p>
               <p><b>【ステップ2】制御のしくみ</b></p>
               <ul>
                 <li>CDK の<b>量はほぼ一定</b></li>
                 <li>サイクリンは<b>合成と分解（ユビキチン－プロテアソーム系）</b>で量が波打つ</li>
                 <li>サイクリンが結合した時期にだけ CDK が活性化し、基質をリン酸化する</li>
               </ul>
               <table>
                 <tr><th>複合体</th><th>働く時期</th><th>役割</th></tr>
                 <tr><td>サイクリンD–CDK4/6</td><td>G1</td><td>Rb をリン酸化して E2F を放し、G1/S 移行へ</td></tr>
                 <tr><td>サイクリンE–CDK2</td><td>G1/S</td><td>S期開始</td></tr>
                 <tr><td>サイクリンA–CDK2</td><td>S</td><td>DNA複製の進行</td></tr>
                 <tr><td>サイクリンB–CDK1</td><td>G2/M</td><td>M期進入（MPF）</td></tr>
               </table>
               <p><b>【ステップ3】なぜ「分解」が要るのか</b></p>
               <p>リン酸化は可逆ですが、<b>タンパク質の分解は不可逆</b>です。
               後期に APC/C がサイクリンBを分解することで、
               細胞周期は<b>後戻りできない一方向の流れ</b>になります。
               「進むかどうか」だけでなく「戻れないようにする」ことが、
               ゲノムを正しく分配するうえで決定的に重要なのです。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "cyc05", ...CAT,
    title: "細胞周期チェックポイント", diff: 220, tags: ["チェックポイント", "p53", "DNA損傷"],
    explain: `<p>細胞周期のチェックポイントと、そこで確認される内容の組み合わせとして<b>誤っている</b>ものはどれか。</p>`,
    type: "radio",
    options: [
      "G1/S チェックポイント — DNAに損傷がないか、細胞の大きさや栄養は十分か",
      "G2/M チェックポイント — DNA複製が完了しているか、損傷がないか",
      "紡錘体形成チェックポイント（M期） — すべての動原体が微小管に正しく結合しているか",
      "G1/S チェックポイント — 細胞質分裂が完了しているか",
    ],
    answer: 3,
    hints: [
      "細胞質分裂は細胞周期のどの段階で起こるでしょうか。G1/S より前でしょうか、後でしょうか。",
      "各チェックポイントは「次の段階に進んでよいか」を、その直前に確認します。",
    ],
    solution: `<p><b>【ステップ1】3つの主要チェックポイント</b></p>
               <table>
                 <tr><th>位置</th><th>確認内容</th><th>主な担い手</th></tr>
                 <tr><td><b>G1/S</b>（制限点）</td><td>DNA損傷、細胞の大きさ、栄養、増殖因子</td><td>p53 → p21 → CDK阻害</td></tr>
                 <tr><td><b>G2/M</b></td><td>DNA複製の完了、DNA損傷</td><td>ATM/ATR → Chk1/2 → Cdc25 抑制</td></tr>
                 <tr><td><b>紡錘体形成</b>（中期→後期）</td><td>全動原体の両極性結合</td><td>Mad2 等 → APC/C 抑制</td></tr>
               </table>
               <p><b>【ステップ2】D が誤りである理由</b></p>
               <p>細胞質分裂は M期の<b>最後</b>に起こります。
               G1/S はその後の次の周期における S期進入の直前で、
               ここで「細胞質分裂が済んだか」を確認するのは順序として意味を成しません。</p>
               <p><b>【ステップ3】p53 が「ゲノムの守護者」と呼ばれる理由</b></p>
               <p>DNA損傷を検知すると p53 が安定化し、</p>
               <ul>
                 <li>軽度の損傷 → p21 を誘導して CDK を止め、<b>修復の時間を稼ぐ</b></li>
                 <li>修復不能 → PUMA/BAX を誘導して<b>アポトーシス</b>させる</li>
               </ul>
               <p>と使い分けます。ヒトのがんの約半数で <i>TP53</i> に変異があるのは、
               この「壊れた細胞を止める／殺す」仕組みを失うことが
               がん化の近道だからです。</p>
               <p>したがって、誤っているのは <b>D</b> です。</p>`,
  },

  {
    id: "cyc06", ...CAT,
    title: "アポトーシスとネクローシス", diff: 190, tags: ["アポトーシス", "ネクローシス", "カスパーゼ"],
    explain: `<p>アポトーシスの特徴として<b>適切でない</b>ものはどれか。</p>`,
    type: "radio",
    options: [
      "細胞が縮小し、クロマチンが凝縮してDNAがヌクレオソーム単位で断片化する。",
      "アポトーシス小体が形成され、マクロファージなどに貪食される。",
      "細胞膜が破れて内容物が流出し、周囲に強い炎症を起こす。",
      "カスパーゼと呼ばれるプロテアーゼのカスケードによって実行される。",
    ],
    answer: 2,
    hints: [
      "アポトーシスは「プログラムされた（秩序ある）細胞死」です。周囲に迷惑をかけるでしょうか。",
      "内容物が漏れて炎症を起こすのは、もう一方の細胞死の特徴です。",
    ],
    solution: `<p><b>【ステップ1】2つの細胞死を対比する</b></p>
               <table>
                 <tr><th></th><th>アポトーシス</th><th>ネクローシス</th></tr>
                 <tr><td>誘因</td><td>生理的なシグナル、修復不能な損傷</td><td>急激な傷害（外傷、虚血、毒物）</td></tr>
                 <tr><td>細胞の体積</td><td><b>縮小</b></td><td><b>膨張</b></td></tr>
                 <tr><td>細胞膜</td><td>保たれたまま小胞化</td><td><b>破裂</b></td></tr>
                 <tr><td>DNA</td><td>ヌクレオソーム単位（約180bp）で断片化<br>→ 電気泳動でラダー</td><td>ランダムに分解<br>→ スメア</td></tr>
                 <tr><td>炎症</td><td><b>起こさない</b></td><td><b>起こす</b></td></tr>
                 <tr><td>エネルギー</td><td>ATP が必要</td><td>不要（むしろ枯渇）</td></tr>
               </table>
               <p><b>【ステップ2】炎症を起こさない仕組み</b></p>
               <p>アポトーシス細胞は膜を保ったまま小さな<b>アポトーシス小体</b>に分かれ、
               表面に<b>ホスファチジルセリン</b>を露出させます。
               これが「私を食べて」という目印になり、
               マクロファージが内容物を漏らさずに丸ごと貪食します。
               だから周囲の組織は影響を受けません。</p>
               <p><b>【ステップ3】2つの実行経路</b></p>
               <ul>
                 <li><b>内因系</b>：ミトコンドリアからシトクロムc が放出 → アポプトソーム → カスパーゼ9 → 実行カスパーゼ3/7</li>
                 <li><b>外因系</b>：Fas/TNF受容体にリガンドが結合 → DISC → カスパーゼ8 → 実行カスパーゼ3/7</li>
               </ul>
               <p class="tiny">ヒトの手指は、水かき部分の細胞がアポトーシスで除かれることで形作られます。
               細胞死は「失敗」ではなく、発生に組み込まれた設計の一部です。</p>
               <p>したがって、適切でないのは <b>C</b> です。</p>`,
  },

  {
    id: "cyc07", ...CAT,
    title: "細胞数から分裂回数を求める", diff: 130, tags: ["計算", "指数増殖", "倍加時間"],
    explain: `<p>1個の細胞が分裂を繰り返して <b>4096 個</b> になった。</p>
              <p>この間に各細胞は何回分裂したか。半角数字で答えなさい。</p>`,
    type: "text",
    answer: ["12"],
    hints: ["1回の分裂で細胞数は2倍になります。n 回なら 2ⁿ 個です。", "2¹⁰ = 1024、2¹¹ = 2048、2¹² = ?"],
    solution: `<p><b>【ステップ1】式を立てる</b></p>
               <p>1個から始めて n 回分裂すると、細胞数は <b>2<sup>n</sup></b> 個になります。<br>
               2<sup>n</sup> = 4096</p>
               <p><b>【ステップ2】2のべき乗をたどる</b></p>
               <p>2<sup>10</sup> = 1024 → 2<sup>11</sup> = 2048 → 2<sup>12</sup> = <b>4096</b></p>
               <p>よって n = <b>12</b> 回。</p>
               <p><b>【ステップ3】感覚をつかむ</b></p>
               <p>指数増殖の速さは直感を裏切ります。倍加時間20分の大腸菌なら、
               12回の分裂はわずか4時間。10時間（30回）では 2<sup>30</sup> ≒ <b>10億個</b>に達します。<br>
               もし制限がなければ2日で地球の質量を超えてしまう計算になり、
               実際には栄養の枯渇と老廃物の蓄積によって定常期に入ります。</p>
               <p class="tiny">なお培養細胞の実験では、この関係から
               「集団倍加数（PDL）= log₂(最終細胞数 / 播種細胞数)」を計算し、
               細胞の老化の指標に使います。</p>
               <p>したがって、答えは <b>12</b> です。</p>`,
  },

  {
    id: "cyc08", ...CAT,
    title: "フローサイトメトリーのヒストグラムを読む", diff: 265, tags: ["フローサイトメトリー", "DNA量", "実験推論"],
    explain: `<p>培養細胞をDNA結合色素で染め、フローサイトメトリーでDNA量の分布を測った。</p>
              <p>薬剤を加えていない対照では 2n に大きなピーク、4n に小さなピーク、その中間になだらかな分布が見られた。</p>
              <p>ある薬剤で処理すると、<b>4n の位置にほとんどの細胞が集まった</b>。
              この薬剤の作用として最も考えられるものはどれか。</p>`,
    type: "radio",
    options: [
      "S期への進入を阻害する（G1/S 移行の阻害）",
      "DNA複製そのものを阻害する",
      "M期の進行を阻害する（紡錘体形成の阻害など）",
      "アポトーシスを誘導する",
    ],
    answer: 2,
    hints: [
      "4n は「DNA複製が終わったが、まだ2つに分かれていない」状態です。細胞周期のどこに当たりますか。",
      "G2期とM期の細胞はどちらも 4n。ここで止まるということは、その先へ進めないということです。",
    ],
    solution: `<p><b>【ステップ1】DNA量と細胞周期の対応</b></p>
               <table>
                 <tr><th>DNA量</th><th>時期</th></tr>
                 <tr><td>2n</td><td>G0/G1期</td></tr>
                 <tr><td>2n〜4n の中間</td><td>S期（複製の途中）</td></tr>
                 <tr><td>4n</td><td><b>G2期 と M期</b></td></tr>
                 <tr><td>2n 未満（sub-G1）</td><td>DNA断片化＝<b>アポトーシス</b></td></tr>
               </table>
               <p><b>【ステップ2】4n に蓄積する意味</b></p>
               <p>複製は完了しているのに、分裂して 2n に戻れていません。
               つまり <b>G2 または M期で停止</b>しています。
               紡錘体形成を阻害すれば、紡錘体形成チェックポイントが働いて
               中期で止まり、まさにこの分布になります。</p>
               <p><b>【ステップ3】他の選択肢なら何が見えるか</b></p>
               <ul>
                 <li>A（G1/S阻害）⇒ <b>2n に蓄積</b>します。4n ではありません。</li>
                 <li>B（複製阻害）⇒ 複製の途中で止まるので、
                     <b>2n と 4n のあいだ</b>に蓄積します（ヒドロキシウレア等）。</li>
                 <li>D（アポトーシス）⇒ DNAが断片化して色素の結合量が減り、
                     <b>2n より左に sub-G1 ピーク</b>が現れます。</li>
               </ul>
               <p><b>【ステップ4】区別のための追加実験</b></p>
               <p>DNA量だけでは G2 と M を区別できません。
               分けるには<b>リン酸化ヒストンH3（Ser10）</b>の抗体で二重染色します。
               これはM期特異的な標識なので、
               4nかつpH3陽性ならM期停止、4nかつpH3陰性ならG2停止と判定できます。</p>
               <p>したがって、正解は <b>C</b> です。</p>`,
  },

  {
    id: "cyc09", ...CAT,
    title: "ヘイフリック限界と細胞老化", diff: 245, tags: ["細胞老化", "テロメア", "ヘイフリック限界"],
    explain: `<p>正常なヒト線維芽細胞は、培養下でおよそ50回分裂すると増殖を停止する（ヘイフリック限界）。</p>
              <p>この現象について<b>誤っている</b>記述はどれか。</p>`,
    type: "radio",
    options: [
      "分裂のたびにテロメアが短縮し、限界に達するとDNA損傷応答が起こって細胞周期が停止する。",
      "停止した細胞は死んでおらず、代謝的に活動を続けながら分裂だけをしなくなる。",
      "テロメラーゼを強制発現させると、この限界を超えて分裂を続けられるようになる。",
      "老化した細胞は完全に不活性で、周囲の細胞に何の影響も与えない。",
    ],
    answer: 3,
    hints: [
      "老化細胞は何かを「分泌」していないでしょうか。SASP という言葉を思い出しましょう。",
      "老化細胞を除去すると、モデルマウスの加齢症状が改善するという報告があります。なぜでしょう。",
    ],
    solution: `<p><b>【ステップ1】細胞老化の引き金</b></p>
               <p>テロメアは分裂のたびに短縮します（末端複製問題）。
               臨界長に達すると、細胞はそれを<b>DNA二本鎖切断と誤認</b>し、
               ATM → p53 → p21 の経路で細胞周期を不可逆的に停止させます。</p>
               <p><b>【ステップ2】老化 ≠ 死</b></p>
               <p>老化細胞は生きています。代謝は活発で、
               むしろ大きく平たくなって長期間生存し続けます。
               分裂能だけを失った状態です（B は正しい）。</p>
               <p>テロメラーゼ（hTERT）を導入するとテロメアが維持され、
               限界を超えて増殖できるようになります（C は正しい）。
               これが不死化細胞株を作る一般的な手法です。</p>
               <p><b>【ステップ3】D が誤りである理由 — SASP</b></p>
               <p>老化細胞は不活性どころか、<b>SASP（老化関連分泌形質）</b>として
               炎症性サイトカイン（IL-6, IL-8）、増殖因子、
               マトリックスメタロプロテアーゼなどを<b>盛んに分泌</b>します。</p>
               <p>これは両刃の剣です。</p>
               <ul>
                 <li><b>短期的には有益</b>：免疫細胞を呼び寄せて損傷細胞を除去させ、創傷治癒を助ける</li>
                 <li><b>長期的には有害</b>：慢性炎症を作り出し、
                     周囲の正常細胞の老化を促し、逆に前がん細胞の増殖を助長する</li>
               </ul>
               <p>老化細胞を選択的に除去する薬（<b>セノリティクス</b>）が
               加齢関連疾患の治療として研究されているのは、この有害な側面を減らすためです。</p>
               <p><b>【ステップ4】老化の二面性</b></p>
               <p>細胞老化はそもそも<b>がん抑制機構</b>として進化しました。
               損傷を蓄えた細胞が増え続けないよう止める仕組みです。
               その代償として個体の老化に寄与する——
               これは<b>拮抗的多面発現</b>の典型例といえます。</p>
               <p>したがって、誤っているのは <b>D</b> です。</p>`,
  },

  {
    id: "cyc10", ...CAT,
    title: "非対称分裂と幹細胞の維持", diff: 290, tags: ["幹細胞", "非対称分裂", "ニッチ"],
    explain: `<p>幹細胞が自己複製能を保ちながら分化細胞も供給できるのは、
              「非対称分裂」によるとされる。</p>
              <p>組織の幹細胞プールを長期間<b>一定に保つ</b>ために必要な条件として、最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "すべての幹細胞が、必ず幹細胞1個と分化細胞1個を生む分裂だけを行う。",
      "集団として平均すると、1回の分裂あたり幹細胞が1個だけ生まれればよい（対称分裂を含んでもよい）。",
      "すべての幹細胞が対称分裂して幹細胞2個を生む。",
      "幹細胞は分裂せず、分化細胞だけが増殖する。",
    ],
    answer: 1,
    hints: [
      "厳密な非対称分裂だけだと、幹細胞が事故で失われたときに補充できません。",
      "重要なのは個々の細胞の運命ではなく、集団としての収支です。",
    ],
    solution: `<p><b>【ステップ1】2つのモデル</b></p>
               <ul>
                 <li><b>厳密な非対称分裂</b>：1個の幹細胞 → 幹細胞1 + 分化細胞1。
                     細胞ごとに運命が決まっている。</li>
                 <li><b>集団的非対称性（ポピュレーション・アシンメトリー）</b>：
                     個々の分裂は幹細胞2個（対称的自己複製）にも分化細胞2個（対称的分化）にもなりうるが、
                     <b>集団平均として</b>幹細胞数が保たれる。</li>
               </ul>
               <p><b>【ステップ2】なぜ厳密モデルでは不十分か</b></p>
               <p>厳密な非対称分裂しか許さないと、幹細胞が損傷や事故で失われたときに
               <b>補充する手段がありません</b>。幹細胞プールは単調に減っていく一方です。
               長寿命の組織（腸上皮は数日で置き換わる）ではこれは致命的です。</p>
               <p><b>【ステップ3】実際に観察されていること</b></p>
               <p>マウス腸陰窩のライブイメージングと系統追跡（lineage tracing）により、
               個々の幹細胞は<b>確率的に</b>運命を選び、
               中立的な競合の結果ひとつのクローンが陰窩を占めていく（中立的ドリフト）ことが示されました。
               それでも陰窩あたりの幹細胞数は一定に保たれます。</p>
               <p><b>【ステップ4】ニッチが収支を決める</b></p>
               <p>この調節を担うのが<b>ニッチ</b>（幹細胞を維持する微小環境）です。
               ニッチが供給できる維持シグナル（Wnt など）の量には限りがあり、
               <b>ニッチに接していられる細胞数が幹細胞数の上限を決めます</b>。
               ニッチからあふれた細胞は分化へ向かいます。</p>
               <p>組織が損傷して幹細胞が減ると、空いたニッチを埋めるように
               対称的自己複製が優勢になり、プールが回復します。
               この<b>柔軟性</b>こそ、集団的非対称性を採る利点です。</p>
               <p><b>【他の選択肢】</b></p>
               <ul>
                 <li>A ⇒ 維持はできますが、損傷からの回復ができません。「最も適切」とはいえません。</li>
                 <li>C ⇒ 幹細胞が指数的に増え、腫瘍化につながります。</li>
                 <li>D ⇒ 幹細胞が供給されず、組織は枯渇します。</li>
               </ul>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "cyc11", ...CAT,
    title: "アポトーシス・ネクローシス・パイロトーシス", diff: 275, tags: ["細胞死", "パイロトーシス", "炎症"],
    explain: `<p>3種類の細胞死を比べたとき、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "アポトーシスは細胞が縮小し膜が保たれるため炎症を起こしにくいが、ネクローシスとパイロトーシスは膜が破れて内容物が漏れるため炎症を伴う。",
      "3つとも膜が保たれるため、いずれも炎症を起こさない。",
      "アポトーシスはカスパーゼに依存しないが、ネクローシスはカスパーゼに依存する。",
      "パイロトーシスは細胞が縮小して断片化し、周囲に貪食される点でアポトーシスと同一である。",
    ],
    answer: 0,
    hints: [
      "「炎症を起こすかどうか」が最大の分かれ目です。膜が破れるかどうかで決まります。",
      "パイロ（pyro）は「火」。炎症性の細胞死です。",
    ],
    solution: `<p><b>【ステップ1】3つを表で対比する</b></p>
               <table>
                 <tr><th></th><th>アポトーシス</th><th>ネクローシス</th><th>パイロトーシス</th></tr>
                 <tr><td>制御</td><td><b>プログラムされている</b></td><td>受動的・偶発的</td><td><b>プログラムされている</b></td></tr>
                 <tr><td>形態</td><td>細胞<b>縮小</b>、クロマチン凝縮、アポトーシス小体</td><td>細胞<b>膨潤</b>、破裂</td><td>膨潤して<b>破裂</b></td></tr>
                 <tr><td>細胞膜</td><td><b>保たれる</b></td><td>破れる</td><td><b>破れる</b>（ガスダーミンDが孔を開ける）</td></tr>
                 <tr><td>主な実行分子</td><td><b>カスパーゼ3/7</b></td><td>（特定の実行酵素なし）</td><td><b>カスパーゼ1</b>（インフラマソーム）</td></tr>
                 <tr><td>炎症</td><td><b>起こしにくい</b></td><td><b>起こす</b></td><td><b>強く起こす</b>（IL-1β, IL-18 放出）</td></tr>
                 <tr><td>意義</td><td>発生・恒常性・不要細胞の除去</td><td>外傷・虚血などの傷害</td><td>感染細胞の排除と免疫の動員</td></tr>
               </table>
               <p><b>【ステップ2】なぜアポトーシスは炎症を起こさないのか</b></p>
               <p>膜が保たれたまま断片化（アポトーシス小体）し、
               表面に露出した<b>ホスファチジルセリン</b>を目印にマクロファージが速やかに貪食するためです。
               細胞内容物（DAMPs）が外に漏れないので、免疫が起動しません。</p>
               <p><b>【ステップ3】パイロトーシスの位置づけ</b></p>
               <p>「プログラムされている」点はアポトーシスと同じですが、
               <b>あえて破裂させて炎症性サイトカインをまき散らす</b>点が正反対です。
               細胞内に侵入した病原体を隠れ家ごと壊し、周囲に警報を出す防御反応と理解してください。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "cyc12", ...CAT,
    title: "選択的オートファジーと非選択的オートファジー", diff: 250, tags: ["オートファジー", "マイトファジー", "Atg32"],
    explain: `<p>オートファジーには、無差別に細胞質を包み込むものと、標的を選んで分解するものがある。</p>
              <p>選択的オートファジーの説明として正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "受容体タンパク質が標的（損傷ミトコンドリアなど）と隔離膜上のLC3/Atg8を橋渡しすることで、特定の対象だけが取り込まれる。酵母のマイトファジーでは Atg32 が受容体として同定された。",
      "飢餓のときにのみ起こり、標的の選別はリソソーム内で行われる。",
      "プロテアソームが標的を認識し、オートファゴソームへ運び込む。",
      "選択的オートファジーではオートファゴソームを形成せず、標的が直接リソソーム膜を通過する。",
    ],
    answer: 0,
    hints: [
      "「選ぶ」ためには、標的と隔離膜の両方に結合できる分子が必要です。",
      "隔離膜側の目印は LC3（酵母では Atg8）です。",
    ],
    solution: `<p><b>【ステップ1】選択性はどう生まれるか</b></p>
               <p>鍵は<b>オートファジー受容体</b>です。受容体は</p>
               <ul>
                 <li>一方の端で<b>標的</b>（損傷ミトコンドリア、凝集タンパク質、細菌など）に結合し、</li>
                 <li>もう一方の <b>LIR モチーフ</b>で隔離膜上の <b>LC3／Atg8</b> に結合する</li>
               </ul>
               <p>この橋渡しによって、隔離膜が標的の周りに引き寄せられ、そこだけを包み込みます。</p>
               <p><b>【ステップ2】代表例</b></p>
               <table>
                 <tr><th>名称</th><th>標的</th><th>受容体の例</th></tr>
                 <tr><td><b>マイトファジー</b></td><td>損傷ミトコンドリア</td><td>酵母：<b>Atg32</b>／哺乳類：PINK1-Parkin 経路、BNIP3, NIX</td></tr>
                 <tr><td>アグリファジー</td><td>タンパク質凝集体</td><td>p62/SQSTM1</td></tr>
                 <tr><td>ゼノファジー</td><td>細胞内に侵入した細菌</td><td>NDP52, オプチニューリン</td></tr>
                 <tr><td>ペキソファジー</td><td>ペルオキシソーム</td><td>—</td></tr>
               </table>
               <p><b>【ステップ3】非選択的オートファジーとの違い</b></p>
               <p>非選択的（バルク）オートファジーは主に<b>飢餓</b>で誘導され、
               細胞質を無差別に包んで分解し、<b>アミノ酸を再供給</b>することが目的です。
               一方の選択的オートファジーは栄養状態によらず起こり、
               <b>品質管理</b>が目的である点が本質的に違います。
               PINK1/Parkin の異常は家族性パーキンソン病の原因となり、
               損傷ミトコンドリアの処理不全が神経変性につながると考えられています。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },
];
